import React from 'react';
import { motion } from 'framer-motion';
import {
  Clock4, Files, HourglassIcon, Unplug, AlertTriangle,
  Workflow, GitBranch, Zap, Inbox, Sparkles,
  ArrowRight, X, Check,
} from 'lucide-react';

/**
 * ProblemSolutionSection — split layout that contrasts the manual pain
 * of running a business on disconnected tools (left) against the
 * automated Formlio way (right). Designed for high marketing impact.
 */

const PAINS = [
  {
    Icon: Clock4,
    title: 'Manual follow-ups eating your day',
    body: 'Hours lost chasing leads, replies, and approvals one email at a time.',
  },
  {
    Icon: Files,
    title: 'Submissions scattered everywhere',
    body: 'Form data buried in inboxes, spreadsheets, and Slack threads — never one source of truth.',
  },
  {
    Icon: HourglassIcon,
    title: 'Approvals that drag for days',
    body: 'Stakeholders forget, statuses get lost, and your team waits — costing real revenue.',
  },
  {
    Icon: Unplug,
    title: 'A stack of tools that don\'t talk',
    body: 'Glue work, copy-paste, broken automations. Your team becomes the integration.',
  },
  {
    Icon: AlertTriangle,
    title: 'Standard form builders stop at "Submit"',
    body: 'Plain forms just collect data — no conditional logic, no approvals, no workflows. You\'re stuck with raw answers and zero outcome.',
  },
];

const SOLUTIONS = [
  {
    Icon: Workflow,
    title: 'Workflows that run themselves',
    body: 'Trigger emails, approvals, CRM updates, and Slack pings automatically — no manual touch.',
  },
  {
    Icon: GitBranch,
    title: 'Smart routing & conditional logic',
    body: 'Send each submission to the right person, team, or tool based on what was filled in.',
  },
  {
    Icon: Zap,
    title: 'Instant real-time notifications',
    body: 'Slack, email, SMS, or webhook the moment a form lands — never miss a hot lead again.',
  },
  {
    Icon: Inbox,
    title: 'One centralized submissions hub',
    body: 'Every response, signed doc, and audit log in one searchable dashboard your whole team can use.',
  },
  {
    Icon: Sparkles,
    title: 'A form builder that actually does the work',
    body: 'Conditional logic, multi-step approvals, e-signatures, and integrations built in — not bolted on. Your forms now drive outcomes, not just data.',
  },
];

const columnVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

export const ProblemSolutionSection = () => {
  return (
    <section
      className="py-20 lg:py-24 bg-gradient-to-b from-slate-50 to-white"
      data-testid="problem-solution-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Section heading */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.4 }}
          className="text-center max-w-3xl mx-auto mb-12 lg:mb-16"
        >
          <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-indigo-600 mb-3">
            The Formlio Difference
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
            <span className="block">Stop firefighting forms</span>
            <span className="block mt-2 bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              Start automating outcomes.
            </span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            Every minute spent chasing submissions is a minute stolen from growing your business. Here's what changes when you switch.
          </p>
        </motion.div>

        {/* Split layout */}
        <div className="relative grid lg:grid-cols-2 gap-6 lg:gap-10">
          {/* Center "vs" divider — desktop only */}
          <div className="hidden lg:flex absolute left-1/2 top-0 bottom-0 -translate-x-1/2 items-center justify-center pointer-events-none z-10">
            <div className="w-px h-full bg-gradient-to-b from-transparent via-slate-200 to-transparent" />
            <div className="absolute bg-white rounded-full border border-slate-200 shadow-sm w-12 h-12 flex items-center justify-center">
              <ArrowRight className="w-5 h-5 text-indigo-500" />
            </div>
          </div>

          {/* LEFT — Pain Points */}
          <motion.div
            variants={columnVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            className="relative rounded-3xl border border-rose-100 bg-white p-6 sm:p-8 shadow-sm"
            data-testid="problem-column"
          >
            <div className="flex items-center gap-2 mb-6">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-rose-50 text-rose-600 ring-1 ring-rose-100">
                <X className="w-5 h-5" strokeWidth={2.5} />
              </span>
              <div>
                <p className="text-[11px] font-bold tracking-widest uppercase text-rose-600">The Old Way</p>
                <h3 className="text-lg font-semibold text-slate-900">Manual chaos</h3>
              </div>
            </div>

            <ul className="space-y-4">
              {PAINS.map((p) => {
                const { Icon, title, body } = p;
                return (
                  <motion.li
                    key={title}
                    variants={itemVariants}
                    className="group flex items-start gap-3.5 p-3 rounded-xl hover:bg-rose-50/40 transition-colors"
                    data-testid={`pain-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-rose-50 text-rose-600 ring-1 ring-rose-100">
                      <Icon className="w-4 h-4" strokeWidth={2.2} />
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 text-sm sm:text-base">{title}</p>
                      <p className="text-sm text-slate-500 leading-snug mt-0.5">{body}</p>
                    </div>
                  </motion.li>
                );
              })}
            </ul>
          </motion.div>

          {/* RIGHT — How Formlio Solves */}
          <motion.div
            variants={columnVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            className="relative rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50/60 via-white to-white p-6 sm:p-8 shadow-[0_8px_30px_-10px_rgba(16,185,129,0.25)] ring-1 ring-emerald-50"
            data-testid="solution-column"
          >
            <div className="flex items-center gap-2 mb-6">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                <Check className="w-5 h-5" strokeWidth={2.8} />
              </span>
              <div>
                <p className="text-[11px] font-bold tracking-widest uppercase text-emerald-700">The Formlio Way</p>
                <h3 className="text-lg font-semibold text-slate-900">Automated outcomes</h3>
              </div>
            </div>

            <ul className="space-y-4">
              {SOLUTIONS.map((s) => {
                const { Icon, title, body } = s;
                return (
                  <motion.li
                    key={title}
                    variants={itemVariants}
                    className="group flex items-start gap-3.5 p-3 rounded-xl hover:bg-emerald-50/60 transition-colors"
                    data-testid={`solution-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                      <Icon className="w-4 h-4" strokeWidth={2.2} />
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 text-sm sm:text-base">{title}</p>
                      <p className="text-sm text-slate-600 leading-snug mt-0.5">{body}</p>
                    </div>
                  </motion.li>
                );
              })}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSolutionSection;
